import { useEffect, useState } from "react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { CheckCircle2, Clock3, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/StatusPill";
import { AutomationBanner } from "@/components/AutomationBanner";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return isMobile;
};

const DetailBody = ({ application, note, setNote, submitting, onDecision, onRetryProvisioning, retryingProvisioning }) => {
  const decided = application?.status !== "pending";
  const decidedAt = application?.decided_at ? format(new Date(application.decided_at), "dd.MM.yyyy HH:mm", { locale: pl }) : null;
  const createdAt = application?.created_at ? format(new Date(application.created_at), "dd.MM.yyyy HH:mm", { locale: pl }) : null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[1.3fr_0.7fr]">
        <Card className="border-border bg-card/95">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-xl">{application?.applicant_display_name || "Kandydat"}</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Discord ID: <span className="font-mono">{application?.applicant_discord_id}</span>
                </p>
              </div>
              <StatusPill status={application?.status} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 rounded-2xl border border-border bg-background/70 p-4 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Data złożenia</p>
                <p className="mt-2 text-sm font-medium">{createdAt}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Status</p>
                <p className="mt-2 text-sm font-medium">{application?.status}</p>
              </div>
              {decidedAt ? (
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Data decyzji</p>
                  <p className="mt-2 text-sm font-medium">{decidedAt}</p>
                </div>
              ) : null}
              {application?.reviewer_name ? (
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Sprawdzający</p>
                  <p className="mt-2 text-sm font-medium">{application.reviewer_name}</p>
                </div>
              ) : null}
            </div>

            <div className="space-y-3">
              {(application?.answers || []).map((answer) => (
                <Card key={answer.question_id} className="border-border bg-background/70">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{answer.question_label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">{answer.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <AutomationBanner provisioning={application?.discord_provisioning} />

          <Card className="border-border bg-card/95">
            <CardHeader>
              <CardTitle className="text-base">Decyzja rekrutacyjna</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                disabled={decided || submitting}
                placeholder="Dodaj notatkę dla zespołu rekrutacji lub kandydata."
                className="min-h-[140px] resize-none bg-background/70"
                data-testid="application-review-note-textarea"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  onClick={() => onDecision("accepted")}
                  disabled={decided || submitting}
                  className="w-full"
                  data-testid="application-approve-button"
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Zaakceptuj
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => onDecision("rejected")}
                  disabled={decided || submitting}
                  className="w-full"
                  data-testid="application-reject-button"
                >
                  <XCircle className="mr-2 h-4 w-4" /> Odrzuć
                </Button>
              </div>
              {application?.status === "accepted" && application?.discord_provisioning?.status !== "success" ? (
                <Button
                  variant="secondary"
                  onClick={onRetryProvisioning}
                  disabled={retryingProvisioning}
                  className="w-full"
                  data-testid="application-retry-provisioning-button"
                >
                  {retryingProvisioning ? "Ponawianie..." : "Ponów dodanie do Discorda"}
                </Button>
              ) : null}
              {decided ? (
                <div className="rounded-2xl border border-border bg-background/70 p-4 text-sm text-muted-foreground">
                  <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
                    <Clock3 className="h-4 w-4 text-primary" /> Decyzja została już zapisana.
                  </div>
                  <p>{application?.reviewer_note || "Brak notatki rekrutera."}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const ApplicationDetail = ({ application, open, onOpenChange, onDecision, submitting, onRetryProvisioning, retryingProvisioning }) => {
  const isMobile = useIsMobile();
  const [note, setNote] = useState(application?.reviewer_note || "");

  useEffect(() => {
    setNote(application?.reviewer_note || "");
  }, [application]);

  const sharedContent = (
    <DetailBody
      application={application}
      note={note}
      setNote={setNote}
      submitting={submitting}
      onDecision={(decision) => onDecision(decision, note)}
      onRetryProvisioning={onRetryProvisioning}
      retryingProvisioning={retryingProvisioning}
    />
  );

  if (!application) return null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92vh] border-border bg-card">
          <DrawerHeader>
            <DrawerTitle>{application.applicant_display_name}</DrawerTitle>
            <DrawerDescription>Pełny widok zgłoszenia i decyzji rekrutacyjnej.</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6">{sharedContent}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl border-border bg-card p-0">
        <DialogHeader className="border-b border-border px-6 py-5">
          <DialogTitle>{application.applicant_display_name}</DialogTitle>
          <DialogDescription>Pełny widok zgłoszenia i panel decyzji.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[82vh] overflow-y-auto px-6 py-6">{sharedContent}</div>
      </DialogContent>
    </Dialog>
  );
};
